import type { OutsidePlanReview } from './outside-plan-payslips';
import {
  CHECK_ALREADY_DONE_MESSAGE,
  xeroCheckAlreadyDone,
  type RefusedStop,
  type SendAllowance,
} from './background-stop-copy';
import type { PayrollApprovalPolicy } from './approval-policy';
import type { PartialSendState } from './partial-send-view';
import type { BackgroundCommand } from './background-command';
import type { PayrollCorrectionReview } from './corrections';
import type { PayrollSummary, PayrollPage } from './bounded-contract';
import type { LowHoursAdvisory } from './low-hours-advisory'; // FU-235
import {
  currentReconciliationStatus,
  currentReconciliationCopy,
  type CurrentReconciliation,
} from './current-reconciliation-view';
export type WorkRunState =
  | 'DRAFT'
  | 'BUILDING'
  | 'HELD'
  | 'READY'
  | 'APPROVED'
  | 'HANDOFF_PENDING'
  | 'PROVIDER_ACCEPTED'
  | 'RECONCILED'
  | 'CLOSED'
  | 'CANCELLED'
  | 'SUPERSEDED';
export const WORK_STATE_LABELS: Record<WorkRunState, string> = {
  DRAFT: 'Not built',
  BUILDING: 'Building payroll',
  HELD: 'Held pay needs review',
  READY: 'Ready for approval',
  APPROVED: 'Approved',
  HANDOFF_PENDING: 'Sending to Xero',
  PROVIDER_ACCEPTED: 'Sent to Xero — checking pending',
  RECONCILED: 'Xero matches',
  CLOSED: 'Closed with payment evidence',
  CANCELLED: 'Cancelled',
  SUPERSEDED: 'Replaced by a later build',
};
export interface PayrollWorkHold {
  /** Xero plan holds only. HOLD holds that worker; MANUAL_HANDOFF blocks approval. */
  action?: 'HOLD' | 'MANUAL_HANDOFF';
  reasonCode?: string;
  date?: string;
  sourceId?: string;
  sourceType?: string;
  sourceRequestId?: string;
  id: string;
  workerId?: string;
  itemOnly?: boolean;
  href?: string;
  reason: string;
  nextStep: string;
  owner: string;
}
/** Combine visible holds while keeping distinct reasons for the same worker. */
export function payrollWorkerHolds(
  view: Pick<PayrollWorkView, 'holds' | 'captureExclusionHolds' | 'provider'>,
  workerId: string,
): PayrollWorkHold[] {
  const seen = new Set<string>();
  return [...view.holds, ...(view.captureExclusionHolds ?? []), ...view.provider.holds].filter(
    (hold) => {
      if (hold.workerId !== workerId) return false;
      const key = JSON.stringify([hold.id, hold.reason]);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    },
  );
}
export interface PayrollWorkProviderEvidence {
  /** Current server custody, separate from retained historical/send diagnostics. */
  currentReconciliation?: CurrentReconciliation;
  status: 'OK' | 'UNAVAILABLE';
  /** Money may already match while detailed evidence still needs saving. */
  retainedEvidencePending?: boolean;
  outsidePlanReview?: OutsidePlanReview;
  planWorkers?: {
    workerId: string;
    outcome: string;
    preservedCount: number;
    /** Approval-bound names, including zero-value carried destinations. */
    destinationItems?: readonly { providerItemId: string; providerItemName: string }[];
    /** Keyed earnings on a worker Statura would write, not yet acknowledged. */
    needsEarningsReview?: boolean;
    preservedLines: {
      providerItemId: string;
      providerItemName?: string;
      units: number;
      amountCents: number;
      rate?: string;
    }[];
    lines: {
      lineKey: string;
      providerItemId: string;
      providerItemName?: string;
      label: string;
      date: string;
      units: number;
      rate?: string;
      amountCents: number;
    }[];
  }[];
  externallyEntered?: {
    id: string;
    workerId: string;
    label: string;
    amountCents: number;
    reason: string;
  }[];
  planDigest?: string;
  planApproved?: boolean;
  /** Any attempt exists, a refusal included: the approved plan can't be re-approved. */
  planFixed?: boolean;
  /** A worker used up their attempts under this plan: this run stays open. Display only. */
  attemptsExhausted?: boolean;
  /** The latest attempt for a worker, not rejected, sits under another plan. */
  earlierPlanAttempt?: boolean;
  /** A Xero check stop that refusals alone explain, and the workers Xero refused. */
  refusedStop?: RefusedStop;
  refusedWorkers?: string[];
  /** Payslips whose answer can still change: the send is waiting on them, nothing to do. */
  waitingToConfirm?: number;
  anyUnconfirmed?: boolean;
  refusedByXero?: boolean;
  planChanged?: boolean;
  dayALeavePrepared?: boolean;
  /** Started and not finished: Day A's loop runs only while its dialog stays open. */
  dayALeaveUnfinished?: boolean;
  /** The last Update replayed its saved check, read at this time. Null: the time couldn't be read. */
  lastCheckReplay?: {
    checkedAt: string | null;
    /** Whether the last Update replayed couldn't be read at all. */
    unsure?: boolean;
  };
  canResume?: boolean;
  earningsReviews?: {
    workerId: string;
    payslipId: string;
    earningsHash: string;
    requestId?: string;
  }[];
  holds: PayrollWorkHold[];
  advisories: string[];
  validationMessages: string[];
  checkerRequired: boolean;
  /** NOT_CHECKED: the latest record isn't a finished whole-run check, so it decides nothing. */
  readback:
    | 'NOT_SENT'
    | 'NOT_CHECKED'
    /** A finished read: everyone sent matched, and the only others were refused and unchanged. */
    | 'SENT_EXCEPT_REFUSED'
    /** A refused worker has no record of Xero's answer. */
    | 'CHECK_IN_XERO'
    /** A finished read: everyone was refused and every draft is as it was. */
    | 'REFUSED_NOT_SENT'
    | 'UNKNOWN'
    | 'MISMATCH'
    | 'LEAVE_CHANGED_AFTER_SEND'
    | 'MATCHED';
}
export interface PayrollWorkView {
  breakPresumptions?: { timesheetId: string; message: string; workerName: string }[];
  approvalPolicy?: PayrollApprovalPolicy;
  corrections?: PayrollCorrectionReview;
  bounded?: { summary: PayrollSummary; workers: PayrollPage; holds: PayrollPage };
  payRunId: string | null;
  /** Names of workers with held timesheets, by staff id. They have no pay lines. */
  heldWorkerNames?: Record<string, string>;
  /** Display names of who owns each stopped task, by public.users.id. */
  stoppedOwnerNames?: Record<string, string>;
  backgroundTasks?:
    | {
        status: 'OK';
        items: Omit<BackgroundCommand, 'leaseToken'>[];
        total: number;
        complete: boolean;
      }
    | { status: 'UNAVAILABLE' };
  /** Read only for a send stopped with no progress: its own reserved Xero calls. */
  sendAllowance?: SendAllowance;
  /** The database refuses unsafe resumes. Absent or false: Resume is not offered. */
  resumeGuardLive?: boolean;
  state: WorkRunState;
  build: { completed: number; total: number; sealed: boolean; capturing?: boolean } | null;
  workers: {
    id: string;
    name: string;
    amountCents: number | null;
    lines?: {
      id: string;
      component: string;
      units: number;
      amountCents: number;
      breakPresumption?: { message: string; timesheetId: string } | null;
    }[];
  }[];
  holds: PayrollWorkHold[];
  provider: PayrollWorkProviderEvidence;
  connectionId: string | null;
  periodId?: string;
  payRunType?: string;
  rosterPeriodId?: string | null;
  payPeriodEnd?: string;
  providerWrite?: 'NONE' | 'STARTED' | 'UNAVAILABLE';
  preparation?: { configured: boolean; observed: boolean } | null;
  partialSend?: PartialSendState;
  connections?: { id: string; label: string }[];
  recovery?: {
    jobId: string;
    revision: number;
    canReauthorise: boolean;
    makerIsCurrentActor: boolean;
  };
  heldReview?: { jobId: string; digest: string; count: number; acknowledged: boolean };
  /** Approved timesheets held out of the next build (capture-exclusions.ts); never paid until re-approved. */
  captureExclusions?: { digest: string; count: number };
  /** Timesheets in this period the build won't take because they aren't approved yet. */
  unapprovedTimesheets?: { status: 'OK'; count: number } | { status: 'UNAVAILABLE' };
  /** FU-235: workers far below their recent hours. Advisory only; never holds or blocks pay. */
  lowHoursAdvisory?: LowHoursAdvisory;
  /** Approved after this build's controlled capture boundary and absent from captured inputs. */
  lateApprovedTimesheets?:
    | {
        status: 'OK';
        rows: {
          id: string;
          workerId: string;
          workerName: string;
          approvedAt: string;
          nextStep: string;
        }[];
      }
    | { status: 'UNAVAILABLE' };
  captureExclusionHolds?: PayrollWorkHold[];
  /** Exact exclusions bound to this build; missing historical custody is never zero. */
  boundCaptureExclusions?:
    | { status: 'OK'; page: PayrollPage; jobId: string; revision: number }
    | { status: 'UNAVAILABLE' };
  nextExclusionPage?: { status: 'OK'; page: PayrollPage } | { status: 'UNAVAILABLE' };
}
export function rebuildWorkViewSelected(mode: { mode: string; source: string }): boolean {
  // READ_FAILED / UNKNOWN_VALUE / NO_ROW are OFF: the legacy page keeps working.
  return mode.source === 'ROW' && mode.mode !== 'OFF';
}
export function payrollWorkCanApprove(view: PayrollWorkView): boolean {
  return (
    view.state === 'READY' &&
    view.build?.sealed === true &&
    view.holds.every((h) => h.itemOnly) &&
    (!view.heldReview || view.heldReview.acknowledged)
  );
}
/** A held worker holds only that worker: the rest can go. A manual step or unmarked hold blocks. */
function onlyPlannedHolds(view: PayrollWorkView): boolean {
  const { holds, planWorkers } = view.provider;
  if (!holds.length) return true;
  return holds.every((h) => h.action === 'HOLD') && (planWorkers?.length ?? 0) > holds.length;
}
/** Xero refused the whole send and its task is stopped: the plan may be corrected and sent again.
 * An unreadable task list says no. Matches the database's approval rule. */
export function payrollWorkCanRecoverRefusedSend(view: PayrollWorkView): boolean {
  return (
    view.state === 'HANDOFF_PENDING' &&
    view.provider.refusedStop === 'WHOLE_RUN' &&
    view.backgroundTasks?.status === 'OK' &&
    !view.backgroundTasks.items.some(
      (task) =>
        ['SEND', 'READBACK'].includes(task.operation) &&
        ['QUEUED', 'RUNNING', 'PAUSED'].includes(task.state),
    )
  );
}
export function payrollWorkCanPush(view: PayrollWorkView): boolean {
  return (
    (view.state === 'APPROVED' ||
      payrollWorkCanRecoverRefusedSend(view) ||
      (view.state === 'HANDOFF_PENDING' && view.provider.canResume === true)) &&
    view.provider.planApproved === true &&
    view.provider.status === 'OK' &&
    onlyPlannedHolds(view) &&
    (view.provider.readback === 'NOT_SENT' ||
      view.provider.canResume === true ||
      payrollWorkCanRecoverRefusedSend(view))
  );
}

function payrollWorkHasRunningCheck(view: PayrollWorkView): boolean {
  return (
    view.backgroundTasks?.status === 'OK' &&
    view.backgroundTasks.items.some(
      (task) =>
        ['QUEUED', 'RUNNING'].includes(task.state) &&
        (task.operation === 'READBACK' || (task.operation === 'SEND' && task.phase === 'READBACK')),
    )
  );
}

/** Role and state hints only; controlled RPCs recheck the current actor. */
export function payrollWorkPermissions(
  view: PayrollWorkView,
  role: string | null | undefined,
  mode: string,
  now = Date.now(),
) {
  const officer = ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'PAYROLL_OFFICER', 'FINANCE_OFFICER'].includes(
    role ?? '',
  );
  const active = officer && mode === 'ACTIVE';
  return {
    officer,
    launch: active && view.state === 'DRAFT' && !!view.connectionId,
    reauthorise: active && view.recovery?.canReauthorise === true,
    approve:
      active &&
      payrollWorkCanApprove(view) &&
      (view.recovery?.makerIsCurrentActor !== true ||
        view.approvalPolicy?.paySecondPerson === false),
    prepareLeave: active && view.state === 'APPROVED' && view.build?.sealed === true,
    approvePlan:
      active &&
      (view.state === 'APPROVED' || payrollWorkCanRecoverRefusedSend(view)) &&
      view.provider.status === 'OK' &&
      onlyPlannedHolds(view) &&
      !!view.provider.planDigest &&
      !!view.provider.planWorkers?.length &&
      !view.provider.planApproved &&
      !view.provider.planFixed &&
      (view.recovery?.makerIsCurrentActor !== true ||
        (view.approvalPolicy?.planSecondPerson === false && !view.provider.checkerRequired)),
    push: active && payrollWorkCanPush(view),
    readback:
      active &&
      view.provider.status === 'OK' &&
      view.provider.readback !== 'NOT_SENT' &&
      !payrollWorkHasRunningCheck(view),
    cancel:
      active &&
      view.providerWrite === 'NONE' &&
      view.corrections?.status === 'OK' &&
      view.corrections.canCancel,
    replace: active && view.corrections?.status === 'OK' && view.corrections.canReplace,
    correct: active && view.corrections?.status === 'OK' && view.corrections.canCorrect,
    close:
      active &&
      view.state === 'RECONCILED' &&
      view.provider.status === 'OK' &&
      view.provider.readback === 'MATCHED' &&
      !payrollWorkHasRunningCheck(view) &&
      currentReconciliationStatus(view.provider.currentReconciliation, now) === 'MATCHED',
  };
}

export type PayrollWorkAction =
  | 'LAUNCH'
  | 'REAUTHORISE'
  | 'ACK_HELD_ITEMS'
  | 'APPROVE'
  | 'PREPARE_XERO'
  | 'REFRESH_XERO'
  | 'APPROVE_PLAN'
  | 'PUSH'
  | 'READBACK'
  | 'CLOSE'
  | 'CANCEL'
  | 'REFRESH';
export type PayrollWorkCard =
  | 'inputs'
  | 'build'
  | 'approve'
  | 'prepare'
  | 'plan'
  | 'send'
  | 'check'
  | 'close'
  | 'history';
export const PAYROLL_WORK_CARDS: { id: PayrollWorkCard; label: string }[] = [
  { id: 'inputs', label: 'Ready inputs' },
  { id: 'build', label: 'Build and review held pay' },
  { id: 'approve', label: 'Approve pay' },
  { id: 'prepare', label: 'Get Xero ready' },
  { id: 'plan', label: 'Approve the Xero plan' },
  { id: 'send', label: 'Send payslips' },
  { id: 'check', label: 'Check Xero' },
  { id: 'close', label: 'Close' },
];
export function payrollWorkActionLabel(action: PayrollWorkAction, view: PayrollWorkView) {
  switch (action) {
    case 'LAUNCH':
      return 'Build pay';
    case 'REAUTHORISE':
      return 'Reauthorise build';
    case 'ACK_HELD_ITEMS':
      return `Acknowledge ${view.heldReview?.count ?? 0} held items`;
    case 'APPROVE':
      return 'Finalise and approve';
    case 'PREPARE_XERO':
      return 'Get Xero ready';
    case 'REFRESH_XERO':
      return 'Update Xero before sending';
    case 'APPROVE_PLAN':
      return view.provider.planChanged
        ? 'Review and approve updated Xero plan'
        : 'Approve Xero plan';
    case 'PUSH': {
      const count = view.provider.planWorkers
        ? view.provider.planWorkers.filter((worker) => worker.outcome === 'Ready to send').length
        : null;
      return view.state === 'HANDOFF_PENDING'
        ? 'Resume unsent payslips to Xero'
        : count === null
          ? 'Send payslips to Xero'
          : `Send ${count} ${count === 1 ? 'payslip' : 'payslips'} to Xero`;
    }
    case 'READBACK':
      return 'Check Xero again';
    case 'CLOSE':
      return 'Close pay run';
    case 'CANCEL':
      return 'Cancel unsent pay run';
    case 'REFRESH':
      return 'Refresh progress';
  }
}
/** Display capabilities only. Every controlled command rechecks its own authority. */
export function payrollWorkJourney(
  view: PayrollWorkView,
  role: string | null | undefined,
  mode: string,
  now = Date.now(),
) {
  const checkDone =
    view.backgroundTasks?.status === 'OK' && xeroCheckAlreadyDone(view.backgroundTasks.items);
  const permissions = payrollWorkPermissions(view, role, mode, now);
  const checkStatus = currentReconciliationStatus(view.provider.currentReconciliation, now);
  const terminal = ['CLOSED', 'CANCELLED', 'SUPERSEDED'].includes(view.state);
  const common = !permissions.officer
    ? 'A payroll officer, finance officer or administrator must record this decision.'
    : mode !== 'ACTIVE'
      ? 'New payroll is not switched on for sending yet. Ask your administrator.'
      : !view.payRunId
        ? 'Create a draft pay run for this period first.'
        : null;
  const payIndependent =
    view.recovery?.makerIsCurrentActor === true && view.approvalPolicy?.paySecondPerson !== false
      ? 'You started this build. A different payroll officer must finalise and approve it.'
      : null;
  const planIndependent =
    view.recovery?.makerIsCurrentActor === true &&
    (view.approvalPolicy?.planSecondPerson !== false || view.provider.checkerRequired)
      ? 'You started this build. A different payroll officer must approve the Xero plan.'
      : null;
  const processed =
    !!view.build && !view.build.capturing && view.build.completed === view.build.total;
  const acknowledgement =
    processed && !!view.heldReview && !view.heldReview.acknowledged && !view.build?.sealed;
  const prepared = view.preparation?.observed === true && view.provider.dayALeavePrepared === true;
  const candidates: {
    id: PayrollWorkAction;
    card: PayrollWorkCard;
    enabled: boolean;
    reason: string;
  }[] = [
    {
      id: 'LAUNCH',
      card: 'build',
      enabled: permissions.launch,
      reason: !view.connectionId
        ? 'Select a payroll connection first.'
        : 'A build already exists. Review its saved progress.',
    },
    {
      id: 'REAUTHORISE',
      card: 'build',
      enabled: permissions.reauthorise,
      reason: 'Resume is available only for an interrupted build.',
    },
    {
      id: 'ACK_HELD_ITEMS',
      card: 'build',
      enabled: acknowledgement,
      reason: view.heldReview?.acknowledged
        ? 'The exact held list has been acknowledged; processing must finish for this revision.'
        : 'Held review becomes available when all pay inputs have been processed, before the build finishes.',
    },
    {
      id: 'APPROVE',
      card: 'approve',
      enabled: permissions.approve,
      reason:
        payIndependent ?? 'Finish processing and acknowledge held items before final approval.',
    },
    {
      id: 'PREPARE_XERO',
      card: 'prepare',
      enabled: permissions.prepareLeave,
      reason: 'Finalise and approve pay before preparing Xero.',
    },
    {
      id: 'REFRESH_XERO',
      card: 'send',
      enabled:
        (permissions.prepareLeave ||
          (permissions.officer && payrollWorkCanRecoverRefusedSend(view))) &&
        mode === 'ACTIVE' &&
        prepared,
      reason: 'Get Xero ready first. Updating Xero keeps your saved settings.',
    },
    {
      id: 'APPROVE_PLAN',
      card: 'plan',
      enabled: permissions.approvePlan && prepared,
      reason:
        (view.provider.planFixed
          ? 'This Xero plan is fixed because sending has started, so it can’t be approved again.'
          : undefined) ??
        planIndependent ??
        (view.provider.planApproved
          ? 'The current Xero plan is already approved.'
          : 'Get Xero ready, then resolve the Xero checks and review the plan.'),
    },
    {
      id: 'PUSH',
      card: 'send',
      enabled: permissions.push && prepared,
      reason: view.provider.planApproved
        ? 'Resolve the Xero checks or confirm the outcome before sending.'
        : 'Review and approve the exact current Xero plan first. A changed plan needs fresh approval.',
    },
    {
      id: 'READBACK',
      card: 'check',
      enabled: permissions.readback && !checkDone,
      reason: payrollWorkHasRunningCheck(view)
        ? 'A Xero check is still running. Refresh to see its progress.'
        : checkDone
          ? CHECK_ALREADY_DONE_MESSAGE
          : 'Xero checking becomes available after sending. An unavailable read never confirms a match.',
    },
    {
      id: 'CLOSE',
      card: 'close',
      enabled: permissions.close && view.provider.attemptsExhausted !== true,
      reason: currentReconciliationCopy(view.provider.currentReconciliation, now).nextStep,
    },
    {
      id: 'CANCEL',
      card: 'history',
      enabled: permissions.cancel,
      reason:
        view.corrections?.status === 'OK' && view.corrections.stopViaSupport
          ? 'To stop this run, contact Statura support.'
          : view.providerWrite === 'UNAVAILABLE' || !view.providerWrite
            ? 'We could not confirm whether anything was sent to Xero. Refresh before cancelling.'
            : 'Sending to Xero has started. Use Correct this pay run or ask Statura support to review the send.',
    },
  ];
  const actions = candidates.map((action) => ({
    ...action,
    enabled: !common && !terminal && action.enabled,
    reason: terminal
      ? 'This run is finished. Review its history or open another period.'
      : (common ?? action.reason),
  }));
  let current: PayrollWorkCard;
  let primary: PayrollWorkAction;
  if (terminal) {
    current = 'history';
    primary = 'REFRESH';
  } else if (!view.build) {
    current = 'build';
    primary = 'LAUNCH';
  } else if (!view.build.sealed) {
    current = 'build';
    primary = acknowledgement
      ? 'ACK_HELD_ITEMS'
      : permissions.reauthorise
        ? 'REAUTHORISE'
        : 'REFRESH';
  } else if (['READY', 'HELD', 'BUILDING'].includes(view.state)) {
    current = 'approve';
    primary = 'APPROVE';
  } else if (view.state === 'RECONCILED') {
    current = permissions.close ? 'close' : 'check';
    primary = permissions.close
      ? 'CLOSE'
      : payrollWorkHasRunningCheck(view) ||
          checkStatus === 'PENDING' ||
          checkStatus === 'UNAVAILABLE'
        ? 'REFRESH'
        : 'READBACK';
  } else if (
    view.state === 'PROVIDER_ACCEPTED' ||
    (view.state === 'HANDOFF_PENDING' &&
      !view.provider.canResume &&
      !payrollWorkCanRecoverRefusedSend(view))
  ) {
    current = 'check';
    primary = 'READBACK';
  } else if (!prepared) {
    current = 'prepare';
    primary = 'PREPARE_XERO';
  } else if (!view.provider.planApproved) {
    current = 'plan';
    primary = 'APPROVE_PLAN';
  } else {
    current = 'send';
    primary = 'PUSH';
  }
  const selected = actions.find((a) => a.id === primary);
  return {
    actions,
    current,
    primary,
    terminal,
    reason:
      selected && !selected.enabled
        ? selected.reason
        : primary === 'REFRESH' && payrollWorkHasRunningCheck(view)
          ? 'A Xero check is still running. Refresh to see its progress.'
          : current === 'build' && primary === 'REFRESH'
            ? 'Statura is processing pay inputs. Refresh to see saved progress.'
            : 'Review the named run and the current action below.',
  };
}
